import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Loader2, CalendarCheck, Scissors, Ban, Clock, DollarSign } from 'lucide-react';
import { addDays, fmtDate, fmtWindow, hoursLabel, money } from '../../../lib/scheduleFormat';

const TAGS = {
  booked: { label: 'Book', done: 'Booked', className: 'border-emerald-300 bg-emerald-50 text-emerald-800', Icon: CalendarCheck },
  trimmed: { label: 'Trim to fit', done: 'Trimmed to fit', className: 'border-amber-300 bg-amber-50 text-amber-900', Icon: Scissors },
  skipped: { label: 'Skip', done: 'Skipped', className: 'border-slate-300 bg-slate-50 text-slate-700', Icon: Ban },
};

const splitWindow = (window) => (window || '').split('–');

// One week's hours, repeated forward. What fits and what does not is shown per
// week before anything is booked — these are hours you pay for.
const CopyHoursDialog = ({ open, weekStart, busy, plan, onPreview, onCommit, onClose }) => {
  const [source, setSource] = useState(weekStart);
  const [weeks, setWeeks] = useState(4);
  const committed = Boolean(plan?.committed);
  const totals = plan?.totals || {};

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="copy-hours-dialog">
        <DialogHeader>
          <DialogTitle>Copy these hours into several weeks</DialogTitle>
          <DialogDescription>
            Kitchen hours only — no shifts. Hours that no longer fit a later week&apos;s agreement are
            trimmed to what the landlord allows, and anything already booked is left alone and listed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Copy the hours from</Label>
            <div className="mt-2 flex flex-wrap gap-2" data-testid="copy-hours-sources">
              {[0, -7, -14].map((offset) => {
                const candidate = addDays(weekStart, offset);
                return (
                  <Button
                    key={offset}
                    type="button"
                    variant={source === candidate ? 'default' : 'outline'}
                    className={source === candidate ? 'bg-amber-600 hover:bg-amber-700' : ''}
                    onClick={() => setSource(candidate)}
                    data-testid={`copy-hours-source-${candidate}`}
                  >
                    Week of {fmtDate(candidate)}
                  </Button>
                );
              })}
            </div>
          </div>

          <div className="sm:max-w-[16rem]">
            <Label htmlFor="copy-hours-weeks">Weeks forward to fill</Label>
            <Input
              id="copy-hours-weeks" type="number" min="1" max="8" value={weeks}
              onChange={(e) => setWeeks(e.target.value)} data-testid="copy-hours-weeks"
            />
            <p className="mt-1 text-xs text-gray-500">
              Between 1 and 8 weeks, starting the week after the one you copy.
            </p>
          </div>

          {plan && (
            <div className="space-y-2" data-testid="copy-hours-result">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                <span className="text-sm font-medium text-gray-900">
                  {totals.bookings || 0} day{totals.bookings === 1 ? '' : 's'} of hours
                </span>
                <span className="flex items-center gap-1 text-sm font-medium text-gray-900">
                  <Clock className="h-4 w-4 text-amber-700" /> {hoursLabel(totals.hours)}
                </span>
                <span className="flex items-center gap-1 text-sm font-medium text-gray-900"
                  data-testid="copy-hours-cost">
                  <DollarSign className="h-4 w-4 text-amber-700" /> {money(totals.cost)}
                </span>
                <span className="text-xs text-gray-600">
                  {committed ? 'now booked' : 'you would be paying for'} · {fmtDate(plan.start)} – {fmtDate(plan.end)}
                </span>
              </div>

              {plan.weeks?.map((week) => {
                const rows = [
                  ...(week.booked || []).map((row) => ({ ...row, kind: 'booked' })),
                  ...(week.trimmed || []).map((row) => ({ ...row, kind: 'trimmed' })),
                  ...(week.skipped || []).map((row) => ({ ...row, kind: 'skipped' })),
                ].sort((a, b) => (a.date < b.date ? -1 : 1));
                return (
                  <div key={week.target_week} className="rounded-lg border border-gray-200 p-3"
                    data-testid={`copy-hours-week-${week.target_week}`}>
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <p className="text-sm font-medium text-gray-900">
                        Week of {fmtDate(week.target_week)}
                      </p>
                      <p className="text-xs text-gray-600">
                        {hoursLabel(week.totals.hours)} · {money(week.totals.cost)}
                        {week.totals.skipped > 0 ? ` · ${week.totals.skipped} not placed` : ''}
                      </p>
                    </div>
                    <div className="mt-1.5 space-y-1 text-xs">
                      {rows.map((row, index) => {
                        const tag = TAGS[row.kind];
                        const [from, to] = splitWindow(row.kind === 'skipped' ? row.requested_window : row.window);
                        return (
                          <div key={`${row.date}-${row.kind}-${index}`} className="flex flex-wrap items-center gap-2"
                            data-testid={`copy-hours-row-${row.date}-${row.kind}`}>
                            <Badge variant="outline" className={`text-[10px] ${tag.className}`}>
                              <tag.Icon className="mr-1 h-3 w-3" />
                              {committed ? tag.done : tag.label}
                            </Badge>
                            <span className="font-medium text-gray-900">{fmtDate(row.date)}</span>
                            <span className="text-gray-700">{fmtWindow(from, to)}</span>
                            {row.kind !== 'skipped' && (
                              <span className="text-gray-600">
                                {hoursLabel(row.hours)} · {money(row.cost)}
                              </span>
                            )}
                            {row.reason && <span className="text-gray-500">— {row.reason}</span>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="copy-hours-close">
            {committed ? 'Done' : 'Cancel'}
          </Button>
          {!committed && (
            <>
              <Button variant="outline" onClick={() => onPreview(source, Number(weeks))}
                disabled={busy || !weeks} data-testid="copy-hours-preview">
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Show what fits
              </Button>
              <Button
                onClick={() => onCommit(source, Number(weeks))}
                disabled={busy || !plan || (totals.bookings || 0) === 0}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="copy-hours-confirm"
              >
                Book {hoursLabel(totals.hours)} · {money(totals.cost)}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CopyHoursDialog;
